// Duplicar el presupuesto entero (botón "Duplicar" de la cabecera de la ficha).
//
// Fecha nueva, nombre y cliente (por defecto el mismo). El servidor crea un
// presupuesto nuevo en borrador con número nuevo (POST /presupuestos/{id}/duplicar);
// en multievento, las jornadas se desplazan los mismos días. Al crearlo se abre.
//
// Props: abierto, presupuesto, onCerrar(), onCreado(nuevo: {id, numero})
import React, { useEffect, useMemo, useState } from 'react';
import { Copy } from 'lucide-react';
import { clientesAPI, presupuestosAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../ui/dialog';

const claseInput = 'w-full rounded-lg border border-[#CBD3CF] px-3 py-2 text-sm text-[#16221D] focus:outline-none focus:border-duetto-primary';
const nombreCliente = (c) => c?.nombre_empresa || c?.nombre_comercial || c?.nombre || 'Cliente sin nombre';

const DuplicarPresupuesto = ({ abierto, presupuesto, onCerrar, onCreado }) => {
  const [fecha, setFecha] = useState('');
  const [nombre, setNombre] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  // U-46: los comensales con alergias son de ese evento; solo se copian si se pide
  const [copiarAlergicos, setCopiarAlergicos] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState(null);

  const presupuestoId = presupuesto?.id;
  const clienteActual = presupuesto?.cliente_id || '';
  const nombreActual = presupuesto?.nombre_evento || '';

  useEffect(() => {
    if (!abierto) return undefined;
    let vivo = true;
    setFecha('');
    setNombre(nombreActual);
    setClienteId(clienteActual);
    setBusqueda('');
    setCopiarAlergicos(false);
    setError(null);
    clientesAPI.getAll({ limit: 10000 })
      .then((r) => {
        if (!vivo) return;
        const lista = r.data?.clientes || r.data || [];
        setClientes([...lista].sort((a, b) => nombreCliente(a).localeCompare(nombreCliente(b))));
      })
      .catch(() => { if (vivo) setClientes([]); });
    return () => { vivo = false; };
  }, [abierto, presupuestoId, clienteActual, nombreActual]);

  const opciones = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const filtrados = q ? clientes.filter(c => nombreCliente(c).toLowerCase().includes(q)) : clientes;
    // El cliente elegido siempre en la lista
    if (clienteId && !filtrados.some(c => c.id === clienteId)) {
      const elegido = clientes.find(c => c.id === clienteId)
        || { id: clienteId, nombre_empresa: presupuesto?.cliente_nombre || 'Cliente actual' };
      return [elegido, ...filtrados.slice(0, 199)];
    }
    return filtrados.slice(0, 200);
  }, [busqueda, clientes, clienteId, presupuesto?.cliente_nombre]);

  const crear = async () => {
    if (!fecha) {
      setError('Elige la fecha del presupuesto nuevo.');
      return;
    }
    setCreando(true);
    setError(null);
    try {
      const { data } = await presupuestosAPI.duplicar(presupuestoId, {
        fecha_evento_nueva: fecha,
        nombre_evento: nombre.trim() || null,
        cliente_id: clienteId && clienteId !== clienteActual ? clienteId : null,
        copiar_alergicos: copiarAlergicos,
      });
      toast({ title: `Presupuesto #${data?.numero} creado`, description: `Copia del #${presupuesto?.numero} en borrador.` });
      if (onCreado) onCreado(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'No se ha podido duplicar');
    } finally {
      setCreando(false);
    }
  };

  return (
    <Dialog open={!!abierto} onOpenChange={(v) => { if (!v && !creando && onCerrar) onCerrar(); }}>
      {abierto && (
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg bg-white p-5 sm:p-6 gap-3" data-testid="modal-duplicar">
          <div className="pr-8">
            <DialogTitle className="text-lg font-extrabold text-[#16221D]">Duplicar el presupuesto #{presupuesto?.numero}</DialogTitle>
            <DialogDescription className="text-sm text-[#5B6B64] mt-1">
              Se crea un presupuesto nuevo en borrador con las mismas líneas, suplementos, observaciones, bebidas, material y dietas
              {presupuesto?.es_multi_evento ? '; las jornadas se mueven los mismos días que la fecha nueva' : ''}.
            </DialogDescription>
          </div>
          <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
            {presupuesto?.es_multi_evento ? 'Fecha de la primera jornada' : 'Fecha del evento'}
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={claseInput} data-testid="duplicar-fecha" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
            Nombre del evento (opcional)
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className={claseInput} data-testid="duplicar-nombre" />
          </label>
          <div className="flex flex-col gap-1 text-xs font-bold text-[#33443C]">
            Cliente
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={claseInput}
              data-testid="duplicar-buscar-cliente"
            />
            <select value={clienteId} onChange={(e) => setClienteId(e.target.value)} className={claseInput} data-testid="duplicar-cliente">
              {opciones.map(c => (
                <option key={c.id} value={c.id}>{nombreCliente(c)}{c.id === clienteActual ? ' (el mismo)' : ''}</option>
              ))}
            </select>
          </div>
          <label className="flex items-start gap-2 text-sm text-[#33443C]">
            <input
              type="checkbox"
              checked={copiarAlergicos}
              onChange={(e) => setCopiarAlergicos(e.target.checked)}
              className="mt-0.5"
              data-testid="duplicar-copiar-alergicos"
            />
            <span>
              Copiar también los comensales con alergias
              <span className="block text-xs text-[#5B6B64]">Sin la validación de cocina: hay que volver a validarlos para el evento nuevo.</span>
            </span>
          </label>
          <p className="text-sm text-[#6B4E00] bg-[#FFF8E6] border border-[#F1DFA8] rounded-lg px-3 py-2" data-testid="duplicar-aviso">
            No se copian facturas, envíos ni órdenes a proveedores. La lista de alérgenos se copia sin la validación de cocina.
          </p>
          {error && (
            <p className="text-sm text-[#8A1C12] bg-[#FDE8E6] border border-[#F5C2BC] rounded-lg px-3 py-2" role="alert">{error}</p>
          )}
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#E1E6E3] pt-3">
            <button
              type="button"
              onClick={() => onCerrar && onCerrar()}
              disabled={creando}
              className="h-[34px] px-3 rounded-lg border border-[#CBD3CF] bg-white text-[13px] font-bold text-[#16221D] hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={crear}
              disabled={creando}
              className="h-[34px] px-3 rounded-lg border border-duetto-primary bg-duetto-primary text-white text-[13px] font-bold flex items-center gap-1.5 hover:bg-opacity-90 disabled:opacity-50"
              data-testid="duplicar-crear"
            >
              <Copy className="w-3.5 h-3.5" />
              {creando ? 'Creando...' : 'Duplicar'}
            </button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default DuplicarPresupuesto;
